// HTTP-klient mot dugnad-tjenesten. Ett sted som snakker med nettet.
//
// TO KRAV FORMER DENNE FILA:
//  1. Den skal kunne kjøres mot en EKTE tjeneste og mot en lokal stubb i test.
//     Derfor tas `hent` (fetch) og `token` inn som parametre — ingen skjult
//     avhengighet til nettleseren, ingen mock-data bakt inn i klienten.
//  2. Den skal aldri finne på data. Hvert kall svarer enten `{ ok: true, data }`
//     eller `{ ok: false, feil }`. Det finnes ikke et tredje utfall der en tom
//     liste dekker over at kallet feilet — det er nettopp den formen som gjør at
//     et UI ser ferdig ut mens det er blindt.
import { klassifiserFeil, nettverksfeil, ugyldigSvarfeil, type ApiFeil } from './feil.js';
import type {
  KanbanSvar, LenkeKropp, LenkeStatusSvar, LenkerSvar, MegSvar, MeldØktSvar,
  NyLenkeSvar, NyttKortKropp, PersonerSvar, RegistrerKropp, RegistrertSvar,
  SprinterSvar, SprintKropp, ØkterSvar, ØktKropp, KortEndring, Vedtak,
} from './typer.js';

export type Utfall<T> = { ok: true; data: T } | { ok: false; feil: ApiFeil };

export interface KlientOppsett {
  /** Rot-URL for API-et, uten etterhengt skråstrek. */
  basis: string;
  /** `fetch`-implementasjonen. Byttes ut i test. */
  hent: typeof fetch;
  /** Persontokenet, hentet ved hvert kall så det følger utlogging med en gang. */
  token: () => string | null;
  /** Varsles ved 401, så skallet kan sende brukeren til innlogging. */
  påUautentisert?: (feil: ApiFeil) => void;
}

interface KallValg {
  /** Sender IKKE Authorization. Brukes av de offentlige registreringsrutene. */
  offentlig?: boolean;
  kropp?: unknown;
}

export interface Klient {
  readonly basis: string;
  // Timer og person
  meg(): Promise<Utfall<MegSvar>>;
  settAnsattnummer(ansattnummer: string | null): Promise<Utfall<MegSvar>>;
  personer(): Promise<Utfall<PersonerSvar>>;
  økter(sok?: Record<string, string | undefined>): Promise<Utfall<ØkterSvar>>;
  meldØkt(kropp: ØktKropp): Promise<Utfall<MeldØktSvar>>;
  endreØkt(id: string, kropp: Partial<ØktKropp>): Promise<Utfall<MeldØktSvar>>;
  attester(id: string, vedtak: Vedtak, begrunnelse?: string): Promise<Utfall<unknown>>;
  sammendrag(fra?: number, til?: number): Promise<Utfall<unknown>>;
  // Selvregistrering (offentlig)
  lenkeStatus(kode: string): Promise<Utfall<LenkeStatusSvar>>;
  registrer(kode: string, kropp: RegistrerKropp): Promise<Utfall<RegistrertSvar>>;
  // Invitasjonslenker (krever `kanAttestere` — eller admin-token, som et web-UI
  // ikke har). Alle tre svarer 403 uten attestasjonsrett.
  lenker(): Promise<Utfall<LenkerSvar>>;
  nyLenke(kropp: LenkeKropp): Promise<Utfall<NyLenkeSvar>>;
  tilbakekallLenke(idEllerPrefiks: string): Promise<Utfall<null>>;
  // Sprinter
  sprinter(): Promise<Utfall<SprinterSvar>>;
  nySprint(kropp: SprintKropp): Promise<Utfall<unknown>>;
  endreSprint(id: string, kropp: Partial<SprintKropp>): Promise<Utfall<unknown>>;
  slettSprint(id: string): Promise<Utfall<null>>;
  // Kanban
  kanban(sok?: { prosjekt?: string; sprint?: string }): Promise<Utfall<KanbanSvar>>;
  nyttKort(kropp: NyttKortKropp): Promise<Utfall<unknown>>;
  endreKort(id: string, kropp: KortEndring): Promise<Utfall<unknown>>;
  slettKort(id: string): Promise<Utfall<null>>;
}

/** Søkestreng av definerte verdier. Tomme felt skal ikke bli «?type=undefined». */
export function spørring(sok: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(sok)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function lagKlient(oppsett: Partial<KlientOppsett> = {}): Klient {
  const basis = (oppsett.basis ?? '/api').replace(/\/+$/, '');
  const hent = oppsett.hent ?? ((...a: Parameters<typeof fetch>) => globalThis.fetch(...a));
  const token = oppsett.token ?? (() => null);

  async function kall<T>(metode: string, sti: string, valg: KallValg = {}): Promise<Utfall<T>> {
    const hoder: Record<string, string> = { accept: 'application/json' };
    if (valg.kropp !== undefined) hoder['content-type'] = 'application/json';
    if (!valg.offentlig) {
      const t = token();
      // Uten token er svaret 401 uansett. Vi lager det selv framfor å sende et
      // kall vi vet blir avvist — og teksten blir den samme som tjenestens.
      if (!t) {
        const feil = klassifiserFeil(401, { feil: 'du er ikke logget inn' });
        oppsett.påUautentisert?.(feil);
        return { ok: false, feil };
      }
      hoder.authorization = `Bearer ${t}`;
    }

    let svar: Response;
    try {
      svar = await hent(basis + sti, {
        method: metode,
        headers: hoder,
        ...(valg.kropp !== undefined ? { body: JSON.stringify(valg.kropp) } : {}),
      });
    } catch (e) {
      return { ok: false, feil: nettverksfeil(e) };
    }

    // 204 og tomt svar: DELETE-rutene i kontrakten svarer uten kropp.
    if (svar.status === 204) return { ok: true, data: null as T };

    let kropp: unknown = null;
    let harKropp = false;
    try {
      const tekst = await svar.text();
      if (tekst.trim()) { kropp = JSON.parse(tekst); harKropp = true; }
    } catch {
      // Ikke-JSON fra en mellomtjener er en egen feil, ikke «ukjent svar».
      if (!svar.ok) return { ok: false, feil: klassifiserFeil(svar.status, null) };
      return { ok: false, feil: ugyldigSvarfeil(svar.status) };
    }

    if (!svar.ok) {
      const feil = klassifiserFeil(svar.status, kropp);
      if (feil.art === 'uautentisert') oppsett.påUautentisert?.(feil);
      return { ok: false, feil };
    }
    if (!harKropp) return { ok: true, data: null as T };
    return { ok: true, data: kropp as T };
  }

  return {
    basis,

    meg: () => kall<MegSvar>('GET', '/meg'),

    // `null` nullstiller. Kontrakten tar imot begge, og vi sender ALDRI
    // ansattnummeret noe annet sted enn hit og til registreringen.
    settAnsattnummer: (ansattnummer) => kall<MegSvar>('PATCH', '/meg', { kropp: { ansattnummer } }),

    personer: () => kall<PersonerSvar>('GET', '/personer'),

    økter: (sok = {}) => kall<ØkterSvar>('GET', `/okter${spørring(sok)}`),

    // Merk hva som IKKE sendes: `personId`, `kilde` og `status`-verdier utover
    // utkast/meldt. Identitet stemples server-side (kontrakten, «Fellesregler»),
    // og en klient som sender den later som den bestemmer noe den ikke gjør.
    meldØkt: (kropp) => kall<MeldØktSvar>('POST', '/okter', { kropp }),
    endreØkt: (id, kropp) => kall<MeldØktSvar>('PATCH', `/okter/${encodeURIComponent(id)}`, { kropp }),
    attester: (id, vedtak, begrunnelse) => kall<unknown>(
      'POST', `/okter/${encodeURIComponent(id)}/attester`,
      { kropp: { vedtak, ...(begrunnelse ? { begrunnelse } : {}) } },
    ),

    sammendrag: (fra, til) => kall<unknown>('GET', `/sammendrag${spørring({ fra, til })}`),

    lenkeStatus: (kode) => kall<LenkeStatusSvar>(
      'GET', `/registrer/${encodeURIComponent(kode)}`, { offentlig: true },
    ),
    registrer: (kode, kropp) => kall<RegistrertSvar>(
      'POST', `/registrer/${encodeURIComponent(kode)}`, { offentlig: true, kropp },
    ),

    lenker: () => kall<LenkerSvar>('GET', '/lenker'),
    nyLenke: (kropp) => kall<NyLenkeSvar>('POST', '/lenker', { kropp }),
    // Tjenesten godtar hele koden ELLER prefikset (≥ 4 tegn). Fra lista har vi
    // bare prefikset — koden finnes ikke lenger noe sted, og det er meningen.
    tilbakekallLenke: (idEllerPrefiks) => kall<null>(
      'DELETE', `/lenker/${encodeURIComponent(idEllerPrefiks)}`,
    ),

    sprinter: () => kall<SprinterSvar>('GET', '/sprinter'),
    nySprint: (kropp) => kall<unknown>('POST', '/sprinter', { kropp }),
    endreSprint: (id, kropp) => kall<unknown>('PATCH', `/sprinter/${encodeURIComponent(id)}`, { kropp }),
    slettSprint: (id) => kall<null>('DELETE', `/sprinter/${encodeURIComponent(id)}`),

    kanban: (sok = {}) => kall<KanbanSvar>('GET', `/kanban${spørring(sok)}`),
    nyttKort: (kropp) => kall<unknown>('POST', '/kanban/kort', { kropp }),
    endreKort: (id, kropp) => kall<unknown>('PATCH', `/kanban/kort/${encodeURIComponent(id)}`, { kropp }),
    slettKort: (id) => kall<null>('DELETE', `/kanban/kort/${encodeURIComponent(id)}`),
  };
}
